import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus, Trash2, Eye, Save, Pencil, Link as LinkIcon, Gift,
  ArrowLeft, AlertTriangle, Undo2, Share2, Check,
} from 'lucide-react';

import { PageShell } from '@/components/brand/PageShell';
import { LoadingState, EmptyState, ErrorState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { ProductImage } from '@/components/brand/ProductImage';
import { formatPrice } from '@/lib/formatPrice';
import { capture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const fieldInput =
  'w-full h-11 px-4 rounded-xl bg-cream-50 border border-border placeholder:text-ink-muted/70 text-ink ' +
  'text-[15px] focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 transition';

const Field = ({ label, required, hint, children }) => (
  <label className="block">
    <span className="block text-[13px] font-medium text-ink-soft mb-1.5">
      {label}{required && <span className="text-clay-600 ml-0.5">*</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-ink-muted mt-1.5">{hint}</span>}
  </label>
);


const statusBadge = {
  available:        { label: 'Available',       cls: 'bg-sage-100 text-sage-700 border-sage-200' },
  reserved:         { label: 'Reserved',        cls: 'bg-cream-200 text-clay-700 border-cream-300' },
  purchased:        { label: 'Gifted',          cls: 'bg-clay-100 text-clay-700 border-clay-200' },
  fully_funded:     { label: 'Fully gifted',    cls: 'bg-clay-100 text-clay-700 border-clay-200' },
  partially_funded: { label: 'Partly gifted',   cls: 'bg-cream-200 text-clay-700 border-cream-300' },
  pending:          { label: 'Pending',         cls: 'bg-cream-200 text-ink-soft border-cream-300' },
};

/* -------------------------------------------------------------------------- */
/*  Essentials coverage — mirrors the Explore Gifts category taxonomy.         */
/*  Aliases catch manual entries ("Nursery", "bath") and scraped categories.   */
/* -------------------------------------------------------------------------- */

const ESSENTIALS = [
  { slug: 'sleeping',      label: 'Sleeping',      aliases: ['sleep', 'cot', 'crib', 'cradle', 'nursery & sleep'] },
  { slug: 'feeding',       label: 'Feeding',       aliases: ['feed', 'bottle', 'nursing'] },
  { slug: 'diapering',     label: 'Diapering',     aliases: ['diaper', 'nappy'] },
  { slug: 'bathing',       label: 'Bath',          aliases: ['bath', 'skincare', 'baby care'] },
  { slug: 'clothing',      label: 'Clothing',      aliases: ['cloth', 'wear', 'apparel'] },
  { slug: 'baby-gear',     label: 'On the move',   aliases: ['gear', 'stroller', 'carrier', 'car seat'] },
  { slug: 'health-safety', label: 'Health',        aliases: ['health', 'safety', 'monitor'] },
  { slug: 'nursery-decor', label: 'Nursery',       aliases: ['nursery', 'decor', 'storage'] },
  { slug: 'playing',       label: 'Play',          aliases: ['play', 'toy', 'development'] },
];

const essentialSlugFor = (rawCategory) => {
  const c = (rawCategory || '').toLowerCase().trim();
  if (!c) return null;
  for (const e of ESSENTIALS) {
    if (c === e.slug || c.includes(e.slug) || e.aliases.some((a) => c.includes(a))) {
      return e.slug;
    }
  }
  return null;
};

const CoverageMeter = ({ items }) => {
  const counts = {};
  for (const it of items) {
    const slug = essentialSlugFor(it.category);
    if (slug) counts[slug] = (counts[slug] || 0) + 1;
  }
  const covered = ESSENTIALS.filter((e) => counts[e.slug] > 0).length;

  return (
    <section className="rounded-3xl bg-cream-50 border border-border/70 shadow-soft-1 p-6 sm:p-8 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-4">
        <div>
          <div className="eyebrow mb-2">Essentials coverage</div>
          <h2 className="font-display text-xl text-ink leading-snug">
            {covered === ESSENTIALS.length
              ? 'Every essential covered. Beautifully done.'
              : `${covered} of ${ESSENTIALS.length} essentials covered.`}
          </h2>
        </div>
        <span className="text-sm text-ink-muted">
          {covered < ESSENTIALS.length && 'Tap a gap to browse that category'}
        </span>
      </div>

      <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden mb-5">
        <div
          className="h-full bg-clay-500 rounded-full transition-all duration-700"
          style={{ width: `${Math.round((covered / ESSENTIALS.length) * 100)}%` }}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {ESSENTIALS.map((e) => {
          const n = counts[e.slug] || 0;
          return n > 0 ? (
            <span
              key={e.slug}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-sage-100 text-sage-700 border border-sage-200"
            >
              <Check className="h-3 w-3" /> {e.label} · {n}
            </span>
          ) : (
            <a
              key={e.slug}
              href={`/explore-gifts?category=${e.slug}`}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] bg-cream-50 text-ink-muted border border-border hover:border-clay-300 hover:text-clay-700 transition-colors"
            >
              <Plus className="h-3 w-3" /> {e.label}
            </a>
          );
        })}
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*  Manage item card — checkbox + remove                                       */
/* -------------------------------------------------------------------------- */

const ManageItemCard = ({ item, selected, onToggleSelect, onRemove, onRelease }) => {
  const status = statusBadge[item.status] || statusBadge.available;
  // Owner can release a reserved/purchased item back to available — the
  // undo for guest mistakes (or cleanup if someone falsely claimed items).
  // Items with contributions on them are money — the server refuses those.
  const releasable =
    ['reserved', 'purchased'].includes(item.status) &&
    !(Number(item.total_contributed) > 0);

  return (
    <article className="group relative flex flex-col rounded-2xl bg-cream-50 border border-border/70 overflow-hidden shadow-soft-1 hover:shadow-soft-2 transition-shadow">
      {/* Checkbox overlay */}
      <label className="absolute top-3 left-3 z-10 inline-flex items-center justify-center h-6 w-6 rounded-md bg-cream-50/95 backdrop-blur-sm ring-1 ring-black/10 cursor-pointer">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(item.id)}
          className="h-3.5 w-3.5"
        />
      </label>

      {/* Image */}
      <div className="relative aspect-square bg-cream-100 overflow-hidden">
        <ProductImage
          src={item.image_url}
          alt={item.name}
          fallbackLabel={item.name}
          className="h-full w-full object-cover"
        />
        <span className={`absolute top-3 right-3 inline-block text-[10.5px] uppercase tracking-[0.16em] px-2 py-1 rounded-full border backdrop-blur-sm ${status.cls}`}>
          {status.label}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 p-4 flex flex-col">
        <h3 className="font-display text-[15px] text-ink leading-snug line-clamp-2">{item.name}</h3>
        {item.description && (
          <p className="mt-1 text-xs text-ink-muted line-clamp-2">{item.description}</p>
        )}
        {item.price ? (
          <p className="mt-2 font-display text-base text-ink">{formatPrice(item.price, item.currency_code || 'INR')}</p>
        ) : null}
        <div className="mt-auto pt-4 space-y-1.5">
          {releasable && (
            <BrandButton
              variant="outline"
              size="sm"
              onClick={() => onRelease(item)}
              className="w-full"
              data-testid={`release-item-${item.id}`}
              title="Return this item to 'available' — use if a guest claimed it by mistake"
            >
              <Undo2 className="h-3.5 w-3.5" /> Release
            </BrandButton>
          )}
          <BrandButton
            variant="ghost"
            size="sm"
            onClick={() => onRemove(item)}
            className="w-full hover:!bg-clay-50 hover:!text-clay-700"
            data-testid={`remove-item-${item.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </BrandButton>
        </div>
      </div>
    </article>
  );
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const RegistryManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();

  const [registry, setRegistry] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Address
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryPincode, setDeliveryPincode] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);

  // Add modals
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddByUrl, setShowAddByUrl] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  const [generatingLink, setGeneratingLink] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', description: '', price: '', currency_code: 'INR',
    category: '', image_url: '', url: '',
  });

  // Bulk + delete
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteRegistry, setShowDeleteRegistry] = useState(false);
  const [deletingRegistry, setDeletingRegistry] = useState(false);

  /* ---------- auth gate + initial fetch ---------- */
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    fetchRegistry();
  }, [authLoading, user, id]); // eslint-disable-line

  const fetchRegistry = async () => {
    try {
      const response = await axios.get(`${API}/registry/${id}`);
      setRegistry(response.data);
      setItems(response.data.items || []);
      setDeliveryAddress(response.data.delivery_address || '');
      setDeliveryPincode(response.data.delivery_pincode || '');
      setDeliveryPhone(response.data.delivery_phone || '');
    } catch (error) {
      toast.error("Couldn't load this registry");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- address ---------- */
  const saveAddress = async () => {
    if (!deliveryAddress.trim()) return toast.error('Please enter an address');
    setSavingAddress(true);
    try {
      await axios.put(
        `${API}/registry/${id}/personalize`,
        {
          delivery_address: deliveryAddress,
          delivery_pincode: deliveryPincode,
          delivery_phone: deliveryPhone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Address updated');
      setRegistry((r) => ({
        ...r,
        delivery_address: deliveryAddress,
        delivery_pincode: deliveryPincode,
        delivery_phone: deliveryPhone,
      }));
      setIsEditingAddress(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const cancelAddressEdit = () => {
    setDeliveryAddress(registry?.delivery_address || '');
    setDeliveryPincode(registry?.delivery_pincode || '');
    setDeliveryPhone(registry?.delivery_phone || '');
    setIsEditingAddress(false);
  };

  /* ---------- visibility ---------- */
  const setPrivacy = async (level) => {
    const prev = registry?.privacy_level || 'public';
    if (level === prev) return;
    setRegistry((r) => ({ ...r, privacy_level: level }));  // optimistic
    try {
      await axios.put(
        `${API}/registry/${id}/personalize`,
        { privacy_level: level },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        level === 'private'
          ? 'Registry is now link-only — hidden from search'
          : 'Registry is findable in search'
      );
    } catch (error) {
      setRegistry((r) => ({ ...r, privacy_level: prev }));  // roll back
      toast.error('Could not update visibility');
    }
  };

  /* ---------- items: add ----------
   * Primary path: navigate to /explore-gifts?addTo={id}, where the user
   * picks from the curated catalog (which has reliable images) and items
   * are saved straight to the registry. Returning here triggers a refetch.
   *
   * Secondary path: "Paste a link" — generate affiliate link in parallel
   * with metadata scrape so the add-item form opens pre-filled. Best-
   * effort: if scrape fails, the form still opens with the URL and the
   * user can type the rest.
   */
  const browseGifts = () => navigate(`/explore-gifts?addTo=${id}`);

  const generateAffiliateAndOpen = async () => {
    if (!productUrl.trim()) return toast.error('Please paste a product URL');
    setGeneratingLink(true);
    try {
      const [linkResp, metaResp] = await Promise.all([
        axios.post(
          `${API}/affiliate/generate-link`,
          { product_url: productUrl, registry_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios
          .post(`${API}/products/fetch-metadata`, { url: productUrl })
          .catch(() => ({ data: {} })),
      ]);

      const affiliate = linkResp.data?.affiliate_link || productUrl;
      const m = metaResp.data || {};

      setNewItem({
        name: m.title || '',
        description: m.description || '',
        price: m.price ? String(Math.round(m.price)) : '',
        currency_code: (m.currency || 'INR').toUpperCase(),
        category: m.category || '',
        image_url: m.image || '',
        url: affiliate,
      });
      setShowAddByUrl(false);
      setProductUrl('');
      setShowAddItem(true);

      if (m.title) toast.success("Details fetched. Review and add.");
      else toast.success('Link saved. Fill in the details below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't generate the link");
    } finally {
      setGeneratingLink(false);
    }
  };

  const addManualItem = () => {
    if (!newItem.name.trim() || !newItem.price) {
      return toast.error('Name and price are required');
    }
    const itemToAdd = {
      id: Date.now().toString(),
      name: newItem.name,
      description: newItem.description,
      price: Number(newItem.price) || 0,
      currency_code: (newItem.currency_code || 'INR').toUpperCase(),
      category: newItem.category,
      // Empty image is fine — ProductImage renders a labeled placeholder.
      // (This previously referenced FALLBACK_IMG, deleted in the
      // ProductImage refactor — a latent ReferenceError on manual add.)
      image_url: newItem.image_url || '',
      url: newItem.url,
      status: 'available',
    };
    capture('item_added', { source: 'manual' });
    setItems((p) => [...p, itemToAdd]);
    setNewItem({
      name: '', description: '', price: '', currency_code: 'INR',
      category: '', image_url: '', url: '',
    });
    setShowAddItem(false);
    toast.success(`"${itemToAdd.name}" added — remember to save your changes`);
  };

  /* ---------- items: remove ---------- */
  const removeItem = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from your basket?`)) return;
    try {
      await axios.delete(`${API}/registry/${id}/items/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((p) => p.filter((i) => i.id !== item.id));
      setSelectedItems((p) => p.filter((sid) => sid !== item.id));
      toast.success('Item removed');
    } catch (error) {
      // If backend says it's not there (likely an unsaved item), still remove locally
      setItems((p) => p.filter((i) => i.id !== item.id));
      setSelectedItems((p) => p.filter((sid) => sid !== item.id));
    }
  };

  const toggleSelect = (itemId) => {
    setSelectedItems((p) =>
      p.includes(itemId) ? p.filter((sid) => sid !== itemId) : [...p, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === items.length) setSelectedItems([]);
    else setSelectedItems(items.map((i) => i.id));
  };

  const removeSelected = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Remove ${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} from your basket?`)) return;
    try {
      await Promise.all(
        selectedItems.map((itemId) =>
          axios.delete(`${API}/registry/${id}/items/${itemId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {})
        )
      );
      setItems((p) => p.filter((i) => !selectedItems.includes(i.id)));
      setSelectedItems([]);
      toast.success('Items removed');
    } catch (error) {
      toast.error('Some items could not be removed');
    }
  };

  /* ---------- save (PUT bulk) ---------- */
  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/registry/${id}/items`,
        items,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Your basket is saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- delete registry ---------- */
  const releaseItem = async (item) => {
    try {
      await axios.post(
        `${API}/registry/${id}/items/${item.id}/unmark-purchase`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${item.name}" is available again`);
      fetchRegistry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not release this item');
    }
  };

  const deleteRegistry = async () => {
    setDeletingRegistry(true);
    try {
      await axios.delete(`${API}/registry/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Registry deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Could not delete the registry');
    } finally {
      setDeletingRegistry(false);
    }
  };

  /* ---------- view ---------- */

  if (authLoading || loading) {
    return <PageShell><LoadingState label="Opening your basket…" /></PageShell>;
  }

  if (!registry) {
    return (
      <PageShell>
        <div className="container-page py-20">
          <ErrorState
            title="This basket isn't here."
            body="It may have been deleted, or the link is wrong."
            action={<BrandButton onClick={() => navigate('/dashboard')}>Back to your registries</BrandButton>}
          />
        </div>
      </PageShell>
    );
  }

  const allSelected = items.length > 0 && selectedItems.length === items.length;
  const someSelected = selectedItems.length > 0 && !allSelected;
  const itemCount = items.length;

  return (
    <PageShell>
      <div className="container-page py-10 sm:py-14">
        {/* Top breadcrumb */}
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-clay-700 transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> All registries
        </button>

        {/* Header */}
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow mb-3">Manage your basket</div>
            <h1 className="font-display text-display-lg text-ink leading-tight">
              {registry.name || `${registry.event_type || 'Your'} basket`}
            </h1>
            <p className="mt-2 text-ink-soft">
              {itemCount} {itemCount === 1 ? 'item' : 'items'} · changes save when you click <span className="text-ink font-medium">Save</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <BrandButton
              variant="outline"
              onClick={() => {
                capture('registry_shared', { channel: 'whatsapp', from: 'manage' });
                const msg = `Our ${registry?.event_type || ''} registry on Joy Basket\n\nView & gift → ${window.location.origin}/registry/${id}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
              }}
              data-testid="share-whatsapp-btn"
              title="Share your registry on WhatsApp"
            >
              <Share2 className="h-4 w-4" /> Share
            </BrandButton>
            <BrandButton
              variant="ghost"
              onClick={() => navigate(`/registry/${id}`)}
              data-testid="view-registry-btn"
            >
              <Eye className="h-4 w-4" /> View as guest
            </BrandButton>
            <BrandButton
              onClick={saveChanges}
              disabled={saving}
              loading={saving}
              data-testid="save-changes-btn"
            >
              <Save className="h-4 w-4" /> Save
            </BrandButton>
          </div>
        </header>

        {/* Address card */}
        <section className="rounded-3xl bg-cream-50 border border-border/70 shadow-soft-1 p-6 sm:p-8 mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="eyebrow mb-2">Delivery address</div>
              <h2 className="font-display text-xl text-ink leading-snug">
                Where every gift will land.
              </h2>
            </div>
            {!isEditingAddress && (
              <BrandButton variant="outline" size="sm" onClick={() => setIsEditingAddress(true)} data-testid="edit-address-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </BrandButton>
            )}
          </div>

          {isEditingAddress ? (
            <div className="space-y-4">
              <textarea
                rows={3}
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="House / flat, street, area, city"
                className={`${fieldInput} h-auto py-3 leading-relaxed resize-none`}
                data-testid="delivery-address-textarea"
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label="PIN code" hint="6 digits — couriers need this">
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={deliveryPincode}
                    onChange={(e) => setDeliveryPincode(e.target.value.replace(/\D/g, ''))}
                    placeholder="560001"
                    className={fieldInput}
                    data-testid="delivery-pincode-input"
                  />
                </Field>
                <Field label="Phone" hint="For delivery updates only">
                  <Input
                    inputMode="tel"
                    value={deliveryPhone}
                    onChange={(e) => setDeliveryPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className={fieldInput}
                    data-testid="delivery-phone-input"
                  />
                </Field>
              </div>
              <div className="flex gap-2">
                <BrandButton
                  size="sm"
                  onClick={saveAddress}
                  disabled={savingAddress}
                  loading={savingAddress}
                  data-testid="save-address-btn"
                >
                  Save address
                </BrandButton>
                <BrandButton variant="outline" size="sm" onClick={cancelAddressEdit}>
                  Cancel
                </BrandButton>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-cream-100 border border-border p-4 text-[14.5px] text-ink-soft leading-relaxed">
              <p className="whitespace-pre-line">
                {registry.delivery_address || (
                  <span className="text-ink-muted italic">No address yet — add one before sharing.</span>
                )}
              </p>
              {(registry.delivery_pincode || registry.delivery_phone) && (
                <p className="mt-2 text-[13px] text-ink-muted">
                  {registry.delivery_pincode && <>PIN {registry.delivery_pincode}</>}
                  {registry.delivery_pincode && registry.delivery_phone && ' · '}
                  {registry.delivery_phone && <>☎ {registry.delivery_phone}</>}
                </p>
              )}
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 text-xs text-ink-muted">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-clay-600 shrink-0" />
            <span>
              This is shared with everyone who buys you a gift. Make sure it's correct and complete.
            </span>
          </div>

          {/* Visibility — who can find this registry */}
          <div className="mt-6 pt-5 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-[13.5px] font-medium text-ink">Who can find this registry?</p>
              <p className="text-xs text-ink-muted mt-0.5">
                Link-only keeps it out of registry search — only people you share the link with can see it.
              </p>
            </div>
            <div className="inline-flex rounded-full bg-cream-100 border border-border p-1" role="radiogroup" aria-label="Registry visibility">
              {[
                { value: 'public', label: 'Findable in search' },
                { value: 'private', label: 'Link only' },
              ].map((opt) => {
                // Anything that isn't explicitly 'private' (incl. legacy
                // 'family') displays as findable — matching search behavior.
                const isPrivate = registry.privacy_level === 'private';
                const active = opt.value === 'private' ? isPrivate : !isPrivate;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setPrivacy(opt.value)}
                    className={`px-3.5 h-8 rounded-full text-[12.5px] font-medium transition-colors whitespace-nowrap
                      ${active ? 'bg-clay-600 text-cream-50' : 'text-ink-soft hover:text-clay-700'}`}
                    data-testid={`privacy-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Essentials coverage — progress + gap nudges into Explore Gifts */}
        {itemCount > 0 && <CoverageMeter items={items} />}

        {/* Items */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <div className="eyebrow mb-2">Your items</div>
              <h2 className="font-display text-xl text-ink leading-snug">
                {itemCount > 0
                  ? `${itemCount} thoughtful ${itemCount === 1 ? 'thing' : 'things'} in your basket.`
                  : "Let's add the first thing."}
              </h2>
            </div>

            <div className="flex flex-wrap gap-2">
              <BrandButton variant="outline" onClick={() => setShowAddByUrl(true)} data-testid="add-by-url-btn">
                <LinkIcon className="h-4 w-4" /> Paste a link
              </BrandButton>
              <BrandButton onClick={browseGifts} data-testid="add-item-btn">
                <Plus className="h-4 w-4" /> Add item
              </BrandButton>
            </div>
          </div>

          {/* Bulk header */}
          {itemCount > 0 && (
            <div className="flex items-center gap-3 mb-5 px-1">
              <label className="inline-flex items-center gap-2 text-sm text-ink-soft cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                  className={someSelected ? 'data-[state=unchecked]:bg-clay-100' : ''}
                />
                <span>{selectedItems.length > 0 ? `${selectedItems.length} selected` : 'Select all'}</span>
              </label>
              {selectedItems.length > 0 && (
                <BrandButton
                  variant="ghost"
                  size="sm"
                  onClick={removeSelected}
                  className="hover:!bg-clay-50 hover:!text-clay-700"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove selected
                </BrandButton>
              )}
            </div>
          )}

          {itemCount === 0 ? (
            <EmptyState
              eyebrow="An empty basket"
              title="Begin with one thing."
              body="Paste a link from any shop, or add the details by hand. Add as you go — there's no rush."
              action={
                <div className="flex gap-2">
                  <BrandButton variant="outline" onClick={() => setShowAddByUrl(true)}>
                    <LinkIcon className="h-4 w-4" /> Paste a link
                  </BrandButton>
                  <BrandButton onClick={browseGifts}>
                    <Plus className="h-4 w-4" /> Add item
                  </BrandButton>
                </div>
              }
            />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5">
              {items.map((item) => (
                <ManageItemCard
                  key={item.id}
                  item={item}
                  selected={selectedItems.includes(item.id)}
                  onToggleSelect={toggleSelect}
                  onRemove={removeItem}
                  onRelease={releaseItem}
                />
              ))}
            </div>
          )}
        </section>

        {/* Danger zone */}
        <section className="mt-20 pt-12 border-t border-border/70">
          <div className="rounded-2xl bg-cream-100 border border-border p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-lg text-ink leading-snug">Delete this registry</h3>
              <p className="mt-1 text-sm text-ink-soft">This can't be undone. Items, contributions, the share link — all of it goes.</p>
            </div>
            <BrandButton
              variant="ghost"
              onClick={() => setShowDeleteRegistry(true)}
              className="!text-clay-700 hover:!bg-clay-50"
              data-testid="delete-registry-btn"
            >
              <Trash2 className="h-4 w-4" /> Delete registry
            </BrandButton>
          </div>
        </section>
      </div>

      {/* ============ Modals ============ */}

      {/* Add by URL */}
      <Dialog open={showAddByUrl} onOpenChange={setShowAddByUrl}>
        <DialogContent className="sm:max-w-[460px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden">
          <div className="px-7 pt-8 pb-2 text-center">
            <LogoMark size={26} className="text-clay-700 mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="font-display text-[24px] leading-tight text-ink">
                Paste a link from any shop.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-sm text-ink-soft">
              We'll fetch the name, image, and price for you. Edit anything before saving.
            </p>
          </div>
          <div className="px-7 pb-7 pt-4 space-y-4">
            <Field label="Product URL" required>
              <Input
                value={productUrl}
                onChange={(e) => setProductUrl(e.target.value)}
                placeholder="https://example.com/lovely-thing"
                className={fieldInput}
                data-testid="product-url-input"
              />
            </Field>
            <div className="flex gap-2 pt-1">
              <BrandButton variant="outline" onClick={() => setShowAddByUrl(false)} className="flex-1">
                Cancel
              </BrandButton>
              <BrandButton
                onClick={generateAffiliateAndOpen}
                disabled={generatingLink}
                loading={generatingLink}
                className="flex-1"
                data-testid="generate-link-btn"
              >
                {generatingLink ? 'Fetching…' : 'Continue'}
              </BrandButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add manual */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="sm:max-w-[520px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
          <div className="px-7 pt-8 pb-2 text-center">
            <LogoMark size={26} className="text-clay-700 mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="font-display text-[24px] leading-tight text-ink">
                Add a thing to your basket.
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-7 pb-7 pt-4 space-y-4">
            <Field label="Name" required>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="The Heirloom Cradle"
                className={fieldInput}
                data-testid="new-item-name"
              />
            </Field>
            <Field label="A line about it" hint="Optional, but helps your guests">
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Solid ash. Made in Jaipur."
                className={fieldInput}
              />
            </Field>
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
              <Field label="Price" required>
                <Input
                  type="number"
                  value={newItem.price}
                  onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                  placeholder="2500"
                  className={fieldInput}
                  data-testid="new-item-price"
                />
              </Field>
              <Field label="Currency">
                <select
                  value={newItem.currency_code}
                  onChange={(e) => setNewItem({ ...newItem, currency_code: e.target.value })}
                  className={`${fieldInput} pr-8`}
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                  <option value="GBP">£ GBP</option>
                  <option value="EUR">€ EUR</option>
                  <option value="AED">AED</option>
                  <option value="SGD">S$ SGD</option>
                </select>
              </Field>
              <Field label="Category" hint="nursery, feeding…">
                <Input
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  placeholder="Nursery"
                  className={fieldInput}
                />
              </Field>
            </div>
            <Field label="Image URL" hint="Optional. We'll use a placeholder if blank.">
              <Input
                value={newItem.image_url}
                onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                placeholder="https://..."
                className={fieldInput}
              />
            </Field>
            <Field label="Product link" hint={newItem.url ? 'Auto-filled from your paste' : 'Optional'}>
              <Input
                value={newItem.url}
                onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                placeholder="https://..."
                className={fieldInput}
              />
            </Field>

            <div className="flex gap-2 pt-2">
              <BrandButton variant="outline" onClick={() => setShowAddItem(false)} className="flex-1">
                Cancel
              </BrandButton>
              <BrandButton onClick={addManualItem} className="flex-1" data-testid="confirm-add-item-btn">
                <Plus className="h-4 w-4" /> Add to basket
              </BrandButton>
            </div>
            <p className="text-xs text-ink-muted text-center">
              Saved locally — click "Save" at the top to keep your changes.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete registry confirmation */}
      <Dialog open={showDeleteRegistry} onOpenChange={setShowDeleteRegistry}>
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink">Delete this registry?</DialogTitle>
          </DialogHeader>
          <p className="text-ink-soft text-sm">
            This is permanent — items, contributions, the share link, all of it.
            People who already have your link will see a "not found" page.
          </p>
          <div className="flex gap-2 pt-2">
            <BrandButton variant="outline" className="flex-1" onClick={() => setShowDeleteRegistry(false)}>
              Cancel
            </BrandButton>
            <BrandButton
              onClick={deleteRegistry}
              disabled={deletingRegistry}
              loading={deletingRegistry}
              className="flex-1 !bg-clay-700 hover:!bg-clay-800"
              data-testid="confirm-delete-registry-btn"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </BrandButton>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default RegistryManage;
